import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import '@/styles/permissionsStyles.scss';
import './RouterMonitoringSettingsPage.scss';
import { 
  FaDatabase, 
  FaSave, 
  FaBell, 
  FaTrash, 
  FaClock,
  FaInfoCircle,
  FaExclamationTriangle
} from 'react-icons/fa';
import sanitizeText from '@/utils/sanitizeText';
import * as Sentry from '@sentry/react';

/**
 * Router Monitoring Settings Page
 * 
 * Provides configuration for:
 * - Data retention periods for monitoring metrics
 * - Automatic data purging settings
 * - Alert thresholds for monitoring alerts
 * - Alert notification configuration
 */
const RouterMonitoringSettingsPage = () => {
  const { apiFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Data Retention Settings
  const [retentionSettings, setRetentionSettings] = useState({
    icmpRawMetricsRetentionDays: 30,
    snmpRawMetricsRetentionDays: 90,
    hourlyAggregationRetentionDays: 90,
    dailyAggregationRetentionDays: 365,
    dataPurgingEnabled: true,
    purgeScheduleHour: 2,
    lastPurgeRun: null,
    lastPurgeDeletedCount: 0
  });

  // Alert Threshold Settings
  const [alertSettings, setAlertSettings] = useState({
    alertingEnabled: true,
    cpuThresholdPercent: 85,
    memoryThresholdPercent: 90,
    packetLossThresholdPercent: 10.0,
    rttThresholdMs: 1000,
    consecutiveFailuresBeforeAlert: 5,
    alertCooldownMinutes: 60,
    outageMinutesBeforeActionItem: 5,
    defaultAssignedUserId: null,
    defaultAssignedDepartmentId: null,
    availabilityAlertPriority: 'High',
    thresholdAlertPriority: 'Medium',
    autoResolveAlerts: true,
    autoResolveOutageAlerts: true,
    autoResolveThresholdAlerts: true,
    includeStatsInAlert: true,
    slaViolationAlertEnabled: false,
    slaGraceThresholdPercent: 1.0,
    slaViolationAlertPriority: 'High',
    bandwidthAlertEnabled: false,
    bandwidthUnderutilizationPercent: 20.0,
    bandwidthAlertPriority: 'Medium',
  });

  useEffect(() => {
    loadSettings();
  }, []);

  // Track changes to avoid auto-saving on mount.
  // Counter: 0 = not loaded, 1 = initial load applied (skip), 2+ = user changed.
  const retentionChangeCountRef = useRef(0);
  const alertChangeCountRef = useRef(0);

  // Auto-save retention settings with debounce — only fires after user changes
  useEffect(() => {
    if (loading) return;
    retentionChangeCountRef.current += 1;
    if (retentionChangeCountRef.current <= 1) return;

    const timer = setTimeout(() => {
      saveRetentionSettings();
    }, 800);
    return () => clearTimeout(timer);
  }, [retentionSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save alert settings with debounce — only fires after user changes
  useEffect(() => {
    if (loading) return;
    alertChangeCountRef.current += 1;
    if (alertChangeCountRef.current <= 1) return;

    const timer = setTimeout(() => {
      saveAlertSettings();
    }, 800);
    return () => clearTimeout(timer);
  }, [alertSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Load both retention and alert settings
   */
  const loadSettings = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load data retention settings
      const retentionResponse = await apiFetch('/api/RouterConfig/monitoring-settings');
      if (retentionResponse.ok) {
        const retentionData = await retentionResponse.json();
        if (retentionData.success && retentionData.data) {
          setRetentionSettings(retentionData.data);
        }
      }

      // Load alert threshold settings
      const alertResponse = await apiFetch('/api/RouterConfig/alert-thresholds');
      if (alertResponse.ok) {
        const alertData = await alertResponse.json();
        if (alertData.success && alertData.data) {
          const data = alertData.data;
          setAlertSettings({
            alertingEnabled: data.alertingEnabled ?? true,
            cpuThresholdPercent: data.cpuThresholdPercent ?? 85,
            memoryThresholdPercent: data.memoryThresholdPercent ?? 90,
            packetLossThresholdPercent: data.packetLossThresholdPercent ?? 10.0,
            rttThresholdMs: data.rttThresholdMs ?? 1000,
            consecutiveFailuresBeforeAlert: data.consecutiveFailuresBeforeAlert ?? 5,
            alertCooldownMinutes: data.alertCooldownMinutes ?? 60,
            outageMinutesBeforeActionItem: data.outageMinutesBeforeActionItem ?? 5,
            defaultAssignedUserId: data.defaultAssignedUserId ?? null,
            defaultAssignedDepartmentId: data.defaultAssignedDepartmentId ?? null,
            availabilityAlertPriority: data.availabilityAlertPriority ?? 'High',
            thresholdAlertPriority: data.thresholdAlertPriority ?? 'Medium',
            autoResolveAlerts: data.autoResolveAlerts ?? true,
            autoResolveOutageAlerts: data.autoResolveOutageAlerts ?? true,
            autoResolveThresholdAlerts: data.autoResolveThresholdAlerts ?? true,
            includeStatsInAlert: data.includeStatsInAlert ?? true,
            slaViolationAlertEnabled: data.slaViolationAlertEnabled ?? false,
            slaGraceThresholdPercent: data.slaGraceThresholdPercent ?? 1.0,
            slaViolationAlertPriority: data.slaViolationAlertPriority ?? 'High',
            bandwidthAlertEnabled: data.bandwidthAlertEnabled ?? false,
            bandwidthUnderutilizationPercent: data.bandwidthUnderutilizationPercent ?? 20.0,
            bandwidthAlertPriority: data.bandwidthAlertPriority ?? 'Medium',
          });
        }
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading settings:', err);
      setError(sanitizeText(err.message || 'Failed to load settings'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Compute a human-readable "effective detection window" string for the
   * Consecutive Failures setting.  The alert service polls every 30 seconds,
   * so N consecutive failures correspond to roughly N × 30 s.
   */
  const EffectiveDetectionWindow = useMemo(() => {
    const totalSeconds = alertSettings.consecutiveFailuresBeforeAlert * 30;
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = totalSeconds / 60;
    return `~${Number.isInteger(minutes) ? minutes : minutes.toFixed(1)} min`;
  }, [alertSettings.consecutiveFailuresBeforeAlert]);

  /**
   * Save data retention settings
   */
  const saveRetentionSettings = async () => {
    setSaving(true);
    try {
      const response = await apiFetch('/api/RouterConfig/monitoring-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(retentionSettings)
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Data retention settings saved successfully');
      } else {
        toast.error(sanitizeText(data.message || 'Failed to save settings'));
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error saving retention settings:', err);
      toast.error('An error occurred while saving settings');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Save alert threshold settings
   */
  const saveAlertSettings = async () => {
    setSaving(true);
    try {
      const response = await apiFetch('/api/RouterConfig/alert-thresholds', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(alertSettings)
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Alert threshold settings saved successfully');
      } else {
        toast.error(sanitizeText(data.message || 'Failed to save settings'));
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error saving alert settings:', err);
      toast.error('An error occurred while saving settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <HexagonLoader />
      </div>
    );
  }

  return (
    <div className="permissions-management router-monitoring-settings">
      <h1><FaDatabase /> Router Monitoring Settings</h1>
      <p className="page-description">
        Configure data retention periods and alert thresholds for router monitoring system.
      </p>

      {error && (
        <div className="error-message" style={{ padding: '15px', background: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c33', marginBottom: '20px' }}>
          <FaExclamationTriangle style={{ marginRight: '8px' }} />
          {error}
        </div>
      )}

      {/* Data Retention Settings */}
      <div className="settings-section">
        <h2><FaTrash /> Data Retention Configuration</h2>
        <p className="section-description">
          Configure how long monitoring data is stored before automatic purging. 
          Longer retention periods provide more historical data but increase database size.
        </p>

        <div className="retention-grid">
          {/* ICMP Raw Metrics */}
          <div className="setting-item">
            <label htmlFor="icmpRetention">
              <strong>ICMP Raw Metrics Retention</strong>
              <span className="setting-description">How long to keep individual ping results</span>
            </label>
            <div className="input-with-unit">
              <input
                id="icmpRetention"
                type="number"
                min="1"
                max="365"
                value={retentionSettings.icmpRawMetricsRetentionDays}
                onChange={(e) => setRetentionSettings({ ...retentionSettings, icmpRawMetricsRetentionDays: parseInt(e.target.value) || 30 })}
              />
              <span className="unit">days</span>
            </div>
          </div>

          {/* SNMP Raw Metrics */}
          <div className="setting-item">
            <label htmlFor="snmpRetention">
              <strong>SNMP Raw Metrics Retention</strong>
              <span className="setting-description">How long to keep individual SNMP poll results</span>
            </label>
            <div className="input-with-unit">
              <input
                id="snmpRetention"
                type="number"
                min="1"
                max="365"
                value={retentionSettings.snmpRawMetricsRetentionDays}
                onChange={(e) => setRetentionSettings({ ...retentionSettings, snmpRawMetricsRetentionDays: parseInt(e.target.value) || 90 })}
              />
              <span className="unit">days</span>
            </div>
          </div>

          {/* Hourly Aggregation */}
          <div className="setting-item">
            <label htmlFor="hourlyRetention">
              <strong>Hourly Aggregation Retention</strong>
              <span className="setting-description">How long to keep hourly summary data</span>
            </label>
            <div className="input-with-unit">
              <input
                id="hourlyRetention"
                type="number"
                min="1"
                max="730"
                value={retentionSettings.hourlyAggregationRetentionDays}
                onChange={(e) => setRetentionSettings({ ...retentionSettings, hourlyAggregationRetentionDays: parseInt(e.target.value) || 90 })}
              />
              <span className="unit">days</span>
            </div>
          </div>

          {/* Daily Aggregation */}
          <div className="setting-item">
            <label htmlFor="dailyRetention">
              <strong>Daily Aggregation Retention</strong>
              <span className="setting-description">How long to keep daily summary data</span>
            </label>
            <div className="input-with-unit">
              <input
                id="dailyRetention"
                type="number"
                min="1"
                max="3650"
                value={retentionSettings.dailyAggregationRetentionDays}
                onChange={(e) => setRetentionSettings({ ...retentionSettings, dailyAggregationRetentionDays: parseInt(e.target.value) || 365 })}
              />
              <span className="unit">days</span>
            </div>
          </div>

          {/* Purge Schedule */}
          <div className="setting-item">
            <label htmlFor="purgeHour">
              <strong>Purge Schedule Hour</strong>
              <span className="setting-description">What hour (0-23) to run automatic purging</span>
            </label>
            <div className="input-with-unit">
              <input
                id="purgeHour"
                type="number"
                min="0"
                max="23"
                value={retentionSettings.purgeScheduleHour}
                onChange={(e) => setRetentionSettings({ ...retentionSettings, purgeScheduleHour: parseInt(e.target.value) || 2 })}
              />
              <span className="unit">:00</span>
            </div>
          </div>

          {/* Enable Purging Toggle */}
          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={retentionSettings.dataPurgingEnabled}
                onChange={(e) => setRetentionSettings({ ...retentionSettings, dataPurgingEnabled: e.target.checked })}
              />
              <strong style={{ marginLeft: '8px' }}>Enable Automatic Data Purging</strong>
            </label>
            <span className="setting-description">Automatically delete old data based on retention settings</span>
          </div>
        </div>

        {/* Last Purge Info */}
        {retentionSettings.lastPurgeRun && (
          <div className="purge-info">
            <FaClock style={{ marginRight: '8px' }} />
            <strong>Last Purge:</strong> {new Date(retentionSettings.lastPurgeRun).toLocaleString()}
            {retentionSettings.lastPurgeDeletedCount > 0 && (
              <span style={{ marginLeft: '15px' }}>
                <strong>Deleted:</strong> {retentionSettings.lastPurgeDeletedCount.toLocaleString()} records
              </span>
            )}
          </div>
        )}

        <div className="info-box" style={{ marginTop: '15px' }}>
          <FaInfoCircle style={{ marginRight: '8px' }} />
          <strong>Recommended Strategy:</strong> Keep raw metrics for short periods (30-90 days), 
          but retain aggregated data longer (1-2 years) for trend analysis with minimal storage impact.
        </div>
      </div>

      {/* Alert Threshold Settings */}
      <div className="settings-section" style={{ marginTop: '30px' }}>
        <h2><FaBell /> Alert Threshold Configuration</h2>
        <p className="section-description">
          Configure when alerts are triggered and how they behave. Alerts create Action Items 
          when thresholds are exceeded or routers become unavailable.
        </p>

        <div className="alert-grid">
          {/* Enable Alerting */}
          <div className="setting-item full-width">
            <label>
              <input
                type="checkbox"
                checked={alertSettings.alertingEnabled}
                onChange={(e) => setAlertSettings({ ...alertSettings, alertingEnabled: e.target.checked })}
              />
              <strong style={{ marginLeft: '8px' }}>Enable Router Monitoring Alerts</strong>
            </label>
            <span className="setting-description">Create Action Items when monitoring detects issues</span>
          </div>

          {/* ── DETECTION THRESHOLDS ─────────────────────────────────────────── */}
          <div className="alert-subsection-header">
            <span>Detection Thresholds</span>
          </div>

          {/* CPU */}
          <div className="setting-item">
            <label htmlFor="cpuThreshold">
              <strong>CPU Threshold</strong>
              <span className="setting-description">Alert when CPU usage exceeds this percentage</span>
            </label>
            <div className="input-with-unit">
              <input
                id="cpuThreshold"
                type="number"
                min="1"
                max="100"
                value={alertSettings.cpuThresholdPercent}
                onChange={(e) => setAlertSettings({ ...alertSettings, cpuThresholdPercent: Math.max(1, parseInt(e.target.value) || 85) })}
                disabled={!alertSettings.alertingEnabled}
              />
              <span className="unit">%</span>
            </div>
          </div>

          {/* Memory */}
          <div className="setting-item">
            <label htmlFor="memoryThreshold">
              <strong>Memory Threshold</strong>
              <span className="setting-description">Alert when memory usage exceeds this percentage</span>
            </label>
            <div className="input-with-unit">
              <input
                id="memoryThreshold"
                type="number"
                min="1"
                max="100"
                value={alertSettings.memoryThresholdPercent}
                onChange={(e) => setAlertSettings({ ...alertSettings, memoryThresholdPercent: Math.max(1, parseInt(e.target.value) || 90) })}
                disabled={!alertSettings.alertingEnabled}
              />
              <span className="unit">%</span>
            </div>
          </div>

          {/* Packet Loss */}
          <div className="setting-item">
            <label htmlFor="packetLossThreshold">
              <strong>Packet Loss Threshold</strong>
              <span className="setting-description">Alert when packet loss exceeds this percentage. Set to 0 to disable packet loss alerting.</span>
            </label>
            <div className="input-with-unit">
              <input
                id="packetLossThreshold"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={alertSettings.packetLossThresholdPercent}
                onChange={(e) => setAlertSettings({ ...alertSettings, packetLossThresholdPercent: Math.max(0, parseFloat(e.target.value) || 0) })}
                disabled={!alertSettings.alertingEnabled}
              />
              <span className="unit">%</span>
            </div>
            {alertSettings.packetLossThresholdPercent === 0 && (
              <span className="setting-description" style={{ color: '#f59e0b', fontWeight: 500 }}>
                <FaInfoCircle style={{ marginRight: 4 }} />
                Packet loss alerting is disabled (threshold is 0)
              </span>
            )}
          </div>

          {/* RTT */}
          <div className="setting-item">
            <label htmlFor="rttThreshold">
              <strong>RTT Threshold</strong>
              <span className="setting-description">Alert when round-trip time exceeds this value</span>
            </label>
            <div className="input-with-unit">
              <input
                id="rttThreshold"
                type="number"
                min="1"
                max="10000"
                value={alertSettings.rttThresholdMs}
                onChange={(e) => setAlertSettings({ ...alertSettings, rttThresholdMs: Math.max(1, parseInt(e.target.value) || 1000) })}
                disabled={!alertSettings.alertingEnabled}
              />
              <span className="unit">ms</span>
            </div>
          </div>

          {/* Consecutive Failures */}
          <div className="setting-item">
            <label htmlFor="consecutiveFailures">
              <strong>Consecutive Failures</strong>
              <span className="setting-description">Number of back-to-back failed checks before any alert fires</span>
            </label>
            <div className="input-with-unit">
              <input
                id="consecutiveFailures"
                type="number"
                min="1"
                max="20"
                value={alertSettings.consecutiveFailuresBeforeAlert}
                onChange={(e) => setAlertSettings({ ...alertSettings, consecutiveFailuresBeforeAlert: parseInt(e.target.value) || 1 })}
                disabled={!alertSettings.alertingEnabled}
              />
              <span className="unit">failures</span>
            </div>
            {/* Effective detection window hint — monitoring polls every 30 seconds */}
            <span className="threshold-hint">
              Detection window: {EffectiveDetectionWindow} at 30 s poll interval
            </span>
          </div>

          {/* ── ALERT TIMING ─────────────────────────────────────────────────── */}
          <div className="alert-subsection-header">
            <span>Alert Timing</span>
          </div>

          {/* Alert Cooldown */}
          <div className="setting-item">
            <label htmlFor="alertCooldown">
              <strong>Alert Cooldown</strong>
              <span className="setting-description">
                Minimum time after a threshold alert resolves before a new one can fire for the same router and metric.
                Applies to CPU / Memory / RTT / Packet Loss — not to Availability alerts.
              </span>
            </label>
            <div className="input-with-unit">
              <input
                id="alertCooldown"
                type="number"
                min="0"
                max="1440"
                value={alertSettings.alertCooldownMinutes}
                onChange={(e) => {
                  // 0 is a valid choice ("no cooldown"), so do not use || fallback
                  const rawValue = parseInt(e.target.value, 10);
                  setAlertSettings({ ...alertSettings, alertCooldownMinutes: isNaN(rawValue) ? 60 : Math.max(0, rawValue) });
                }}
                disabled={!alertSettings.alertingEnabled}
              />
              <span className="unit">minutes</span>
            </div>
          </div>

          {/* Outage Duration Before Action Item */}
          <div className="setting-item">
            <label htmlFor="outageMinutes">
              <strong>Outage Duration Before Action Item</strong>
              <span className="setting-description">
                Availability alerts only — minimum sustained outage duration before an Action Item is created.
                Suppresses transient router blips and brief reboots. Set to 0 to disable this filter.
              </span>
            </label>
            <div className="input-with-unit">
              <input
                id="outageMinutes"
                type="number"
                min="0"
                max="1440"
                value={alertSettings.outageMinutesBeforeActionItem}
                onChange={(e) => {
                  // 0 is a valid choice ("no transient filter"), so do not use || fallback
                  const rawValue = parseInt(e.target.value, 10);
                  setAlertSettings({ ...alertSettings, outageMinutesBeforeActionItem: isNaN(rawValue) ? 5 : Math.max(0, rawValue) });
                }}
                disabled={!alertSettings.alertingEnabled}
              />
              <span className="unit">minutes</span>
            </div>
            {/* Warn users that 0 means instant action-item creation on N failures */}
            {alertSettings.alertingEnabled && alertSettings.outageMinutesBeforeActionItem === 0 && (
              <div className="setting-warning">
                <FaExclamationTriangle />
                No transient-outage filter — Availability Action Items are created as soon as {alertSettings.consecutiveFailuresBeforeAlert} consecutive {alertSettings.consecutiveFailuresBeforeAlert === 1 ? 'failure is' : 'failures are'} confirmed ({EffectiveDetectionWindow}). Increase this value to suppress brief network blips.
              </div>
            )}
          </div>

          {/* ── ALERT PRIORITY ───────────────────────────────────────────────── */}
          <div className="alert-subsection-header">
            <span>Alert Priority</span>
          </div>

          {/* Availability Priority */}
          <div className="setting-item">
            <label htmlFor="availabilityPriority">
              <strong>Availability Alert Priority</strong>
              <span className="setting-description">Priority assigned to Action Items created when a router is unreachable</span>
            </label>
            <select
              id="availabilityPriority"
              value={alertSettings.availabilityAlertPriority}
              onChange={(e) => setAlertSettings({ ...alertSettings, availabilityAlertPriority: e.target.value })}
              disabled={!alertSettings.alertingEnabled}
            >
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Critical">Critical</option>
            </select>
          </div>

          {/* Threshold Priority */}
          <div className="setting-item">
            <label htmlFor="thresholdPriority">
              <strong>Threshold Alert Priority</strong>
              <span className="setting-description">Priority assigned to Action Items created for CPU / Memory / RTT / Packet Loss breaches</span>
            </label>
            <select
              id="thresholdPriority"
              value={alertSettings.thresholdAlertPriority}
              onChange={(e) => setAlertSettings({ ...alertSettings, thresholdAlertPriority: e.target.value })}
              disabled={!alertSettings.alertingEnabled}
            >
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Critical">Critical</option>
            </select>
          </div>

          {/* ── BEHAVIOUR ────────────────────────────────────────────────────── */}
          <div className="alert-subsection-header">
            <span>Behaviour</span>
          </div>

          {/* Auto-Resolve Master Toggle */}
          <div className="setting-item full-width">
            <label>
              <input
                type="checkbox"
                checked={alertSettings.autoResolveAlerts}
                onChange={(e) => setAlertSettings({ ...alertSettings, autoResolveAlerts: e.target.checked })}
                disabled={!alertSettings.alertingEnabled}
              />
              <strong style={{ marginLeft: '8px' }}>Auto-Resolve Alerts</strong>
            </label>
            <span className="setting-description">Master switch for automatic resolution. When disabled, no alerts will be auto-resolved regardless of the settings below.</span>
          </div>

          {/* Auto-Resolve Outage Alerts */}
          <div className="setting-item full-width" style={{ paddingLeft: '28px' }}>
            <label>
              <input
                type="checkbox"
                checked={alertSettings.autoResolveOutageAlerts}
                onChange={(e) => setAlertSettings({ ...alertSettings, autoResolveOutageAlerts: e.target.checked })}
                disabled={!alertSettings.alertingEnabled || !alertSettings.autoResolveAlerts}
              />
              <strong style={{ marginLeft: '8px' }}>Auto-Resolve Outage Alerts</strong>
            </label>
            <span className="setting-description">Automatically mark availability/outage Action Items as Solved when the router comes back online. Disable to require manual closure for outage events.</span>
          </div>

          {/* Auto-Resolve Threshold Alerts */}
          <div className="setting-item full-width" style={{ paddingLeft: '28px' }}>
            <label>
              <input
                type="checkbox"
                checked={alertSettings.autoResolveThresholdAlerts}
                onChange={(e) => setAlertSettings({ ...alertSettings, autoResolveThresholdAlerts: e.target.checked })}
                disabled={!alertSettings.alertingEnabled || !alertSettings.autoResolveAlerts}
              />
              <strong style={{ marginLeft: '8px' }}>Auto-Resolve Threshold Alerts</strong>
            </label>
            <span className="setting-description">Automatically mark threshold-based Action Items (CPU, memory, packet loss, RTT) as Solved when the metric drops below the configured threshold.</span>
          </div>

          {/* Include Stats */}
          <div className="setting-item full-width">
            <label>
              <input
                type="checkbox"
                checked={alertSettings.includeStatsInAlert}
                onChange={(e) => setAlertSettings({ ...alertSettings, includeStatsInAlert: e.target.checked })}
                disabled={!alertSettings.alertingEnabled}
              />
              <strong style={{ marginLeft: '8px' }}>Include Statistics in Alert</strong>
            </label>
            <span className="setting-description">Embed recent router metrics (CPU, memory, RTT, packet loss history) in the Action Item description for faster diagnosis</span>
          </div>

          {/* ── SLA & BANDWIDTH MONITORING ───────────────────────────────────── */}
          <div className="alert-subsection-header">
            <span>SLA &amp; Bandwidth Monitoring</span>
          </div>

          <div className="setting-item full-width" style={{ gridColumn: '1 / -1' }}>
            <p className="setting-description" style={{ margin: 0 }}>
              Trigger alerts when contracted SLA or bandwidth expectations are not met.
              Requires ISP contracts with SLA% or expected throughput values to be configured
              on the router&apos;s public IP addresses.
            </p>
          </div>

          {/* SLA Violation */}
          <div className="setting-item full-width">
            <label>
              <input
                type="checkbox"
                checked={alertSettings.slaViolationAlertEnabled}
                onChange={(e) => setAlertSettings({ ...alertSettings, slaViolationAlertEnabled: e.target.checked })}
              />
              <strong style={{ marginLeft: '8px' }}>Enable SLA Violation Alerts</strong>
            </label>
            <span className="setting-description">
              Create an Action Item when a router&apos;s 30-day uptime falls below its contracted SLA (minus grace threshold).
            </span>
          </div>

          {alertSettings.slaViolationAlertEnabled && (
            <>
              <div className="setting-item">
                <label htmlFor="slaGraceThreshold">
                  <strong>SLA Grace Threshold</strong>
                  <span className="setting-description">
                    Allowable deviation below the contracted SLA before alerting.
                    Example: SLA 99.9%, grace 1% → alert fires below 98.9%.
                  </span>
                </label>
                <div className="input-with-unit">
                  <input
                    id="slaGraceThreshold"
                    type="number"
                    min="0"
                    max="10"
                    step="0.1"
                    value={alertSettings.slaGraceThresholdPercent}
                    onChange={(e) => {
                      const rawValue = parseFloat(e.target.value);
                      setAlertSettings({ ...alertSettings, slaGraceThresholdPercent: isNaN(rawValue) ? 1.0 : rawValue });
                    }}
                  />
                  <span className="unit">%</span>
                </div>
              </div>

              <div className="setting-item">
                <label htmlFor="slaViolationPriority">
                  <strong>SLA Violation Alert Priority</strong>
                  <span className="setting-description">Priority for SLA violation action items</span>
                </label>
                <select
                  id="slaViolationPriority"
                  value={alertSettings.slaViolationAlertPriority}
                  onChange={(e) => setAlertSettings({ ...alertSettings, slaViolationAlertPriority: e.target.value })}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
            </>
          )}

          {/* Bandwidth Constraint */}
          <div className="setting-item full-width">
            <label>
              <input
                type="checkbox"
                checked={alertSettings.bandwidthAlertEnabled}
                onChange={(e) => setAlertSettings({ ...alertSettings, bandwidthAlertEnabled: e.target.checked })}
              />
              <strong style={{ marginLeft: '8px' }}>Enable Bandwidth Constraint Alerts</strong>
            </label>
            <span className="setting-description">
              Create an Action Item when measured throughput is significantly below contracted speeds.
              Requires ISP contracts with expected download/upload speeds to be configured.
            </span>
          </div>

          {alertSettings.bandwidthAlertEnabled && (
            <>
              <div className="setting-item">
                <label htmlFor="bandwidthThreshold">
                  <strong>Bandwidth Underperformance Threshold</strong>
                  <span className="setting-description">
                    Alert when actual throughput is more than this % below expected contracted speed.
                    Example: 20% → alert fires when throughput falls below 80% of contracted.
                  </span>
                </label>
                <div className="input-with-unit">
                  <input
                    id="bandwidthThreshold"
                    type="number"
                    min="1"
                    max="99"
                    step="1"
                    value={alertSettings.bandwidthUnderutilizationPercent}
                    onChange={(e) => {
                      const rawValue = parseFloat(e.target.value);
                      setAlertSettings({ ...alertSettings, bandwidthUnderutilizationPercent: isNaN(rawValue) ? 20.0 : rawValue });
                    }}
                  />
                  <span className="unit">%</span>
                </div>
              </div>

              <div className="setting-item">
                <label htmlFor="bandwidthAlertPriority">
                  <strong>Bandwidth Alert Priority</strong>
                  <span className="setting-description">Priority for bandwidth constraint action items</span>
                </label>
                <select
                  id="bandwidthAlertPriority"
                  value={alertSettings.bandwidthAlertPriority}
                  onChange={(e) => setAlertSettings({ ...alertSettings, bandwidthAlertPriority: e.target.value })}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
            </>
          )}
        </div>

        <div className="info-box" style={{ marginTop: '15px' }}>
          <FaInfoCircle style={{ marginRight: '8px' }} />
          <div>
            <strong>How the alert gates work:</strong>
            <ul style={{ margin: '6px 0 0 0', paddingLeft: '18px', lineHeight: '1.7' }}>
              <li><strong>All alert types:</strong> Consecutive Failures must be confirmed before any action item fires.</li>
              <li><strong>Availability only:</strong> Outage Duration filter adds an extra delay after N failures are confirmed, suppressing brief transient outages.</li>
              <li><strong>Threshold alerts (CPU / Memory / RTT / Packet Loss):</strong> Alert Cooldown prevents re-firing for the same router and metric within the configured window after an alert resolves.</li>
            </ul>
            <p style={{ margin: '8px 0 0 0' }}>
              Action Items are created using the <em>"IT-NetworkMonitoring"</em> template and assigned to the IT department by default.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouterMonitoringSettingsPage;
